/**
 * YOLO26 Detector using TensorFlow.js (WebGL backend)
 *
 * Standard 3-channel RGB input.
 *
 * Input: [1, 320, 320, 3] NHWC format, normalized 0-1
 * Output: [1, 300, 6] end2end format [x1, y1, x2, y2, score, class_id]
 *
 * TensorFlow.js with WebGL backend is fallback when WebGPU is not available.
 */

import * as tf from '@tensorflow/tfjs';
import '@tensorflow/tfjs-backend-webgpu';  // Register WebGPU backend
import { applyWebGPUShaderPatch } from './webgpuShaderPatch.js';

// Apply WebGPU shader patch for TF.js type mismatch bugs (Chrome 141+)
// Must be called before WebGPU backend initialization
applyWebGPUShaderPatch();

const DEFAULT_INPUT_SIZE = 320;
const DEFAULT_CONFIDENCE_THRESHOLD = 0.15;
const DEFAULT_IOU_THRESHOLD = 0.45;
const NUM_CHANNELS = 3;  // RGB only

// YOLO26 output formats:
// - end2end: [batch, max_det, 6] where max_det is 10-300 and 6 = [x1, y1, x2, y2, score, class_id] (NMS in model)
// - raw: [batch, 2100, 6] where 6 = [x1, y1, x2, y2, score_class0, score_class1] (NMS in JS)
const YOLO26_END2END_DETECTIONS = 300;  // Max possible value for end2end format
const YOLO26_RAW_PREDICTIONS = 2100;
const YOLO26_OUTPUT_FEATURES = 6;

/**
 * Initialize TensorFlow.js with specified backend
 * Always reinitialize to avoid stale WebGPU state after page refresh
 * @param {string} backend - 'webgpu', 'webgl', or 'auto' (tries WebGPU first, falls back to WebGL)
 * @returns {string} The backend that was successfully initialized
 */
async function initializeTensorFlow(backend = 'auto') {
  const currentBackend = tf.getBackend();
  console.log(`[TF.js] Initializing backend: ${backend} (current: ${currentBackend})`);

  // Clean up stale state - critical for Safari WebGPU after page refresh
  try {
    tf.disposeVariables();
    // If WebGPU already set, reset by switching to CPU first
    if (currentBackend === 'webgpu' && (backend === 'auto' || backend === 'webgpu')) {
      console.log('[TF.js] Resetting WebGPU backend');
      await tf.setBackend('cpu');
      await tf.ready();
    }
  } catch (_e) {
    // Ignore cleanup errors
  }

  // Auto mode: try WebGPU first, then WebGL
  if (backend === 'auto' || backend === 'webgpu') {
    try {
      await tf.setBackend('webgpu');
      await tf.ready();
      console.log(`[TF.js] Using WebGPU backend`);
      return 'webgpu';
    } catch (e) {
      if (backend === 'webgpu') {
        console.error('[TF.js] WebGPU failed:', e.message);
        throw new Error(`WebGPU backend failed: ${e.message}`);
      }
      console.warn('[TF.js] WebGPU not available, falling back to WebGL:', e.message);
    }
  }

  // WebGL backend (fallback or explicit)
  await tf.setBackend('webgl');
  await tf.ready();
  console.log('[TF.js] Using WebGL backend');
  return 'webgl';
}

/**
 * TensorFlow.js YOLO26 Detector
 */
export class TfjsYoloDetector {
  constructor(modelPath, options = {}) {
    this.modelPath = modelPath;
    this.inputSize = options.inputSize || DEFAULT_INPUT_SIZE;
    this.confThreshold = options.confThreshold || DEFAULT_CONFIDENCE_THRESHOLD;
    this.iouThreshold = options.iouThreshold || DEFAULT_IOU_THRESHOLD;
    this.classNames = options.classNames || ['ball', 'paddle'];
    this.backend = options.backend || 'auto';  // 'webgpu', 'webgl', or 'auto' (WebGPU with WebGL fallback)

    this.model = null;
    this.isInitialized = false;

    // Model version info (populated from manifest during initialization)
    this.modelInfo = {
      modelName: null,
      modelJson: null,
      deployedAt: null
    };
  }

  async initialize(timeout = 30000) {
    if (this.isInitialized) return true;

    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error(`Model initialization timed out after ${timeout}ms`)), timeout);
    });

    await Promise.race([
      this._doInitialize(),
      timeoutPromise
    ]);
    return true;
  }

  async _doInitialize() {
    const actualBackend = await initializeTensorFlow(this.backend);
    this.actualBackend = actualBackend;  // Store the backend that was actually used

    const modelDir = this.modelPath.replace(/\/[^/]+$/, '');
    let actualModelJsonUrl = this.modelPath;

    try {
      // Try unified manifest first
      let manifest = null;
      const unifiedUrl = `${modelDir}/manifest.detector.json?_=${Date.now()}`;
      const unifiedResp = await fetch(unifiedUrl, { cache: 'no-store' });
      if (unifiedResp.ok) {
        manifest = await unifiedResp.json();
        if (manifest.tfjsModel) {
          actualModelJsonUrl = `${modelDir}/${manifest.tfjsModel}`;
          this.modelInfo = {
            modelName: manifest.modelName || null,
            modelJson: manifest.tfjsModel,
            deployedAt: manifest.deployedAt || null
          };
        }
      } else {
        // Fall back to TFJS-specific manifest
        const tfjsUrl = `${modelDir}/manifest.yolo.detector.json?_=${Date.now()}`;
        const tfjsResp = await fetch(tfjsUrl, { cache: 'no-store' });
        if (tfjsResp.ok) {
          manifest = await tfjsResp.json();
          if (manifest.modelJson) {
            actualModelJsonUrl = `${modelDir}/${manifest.modelJson}`;
            this.modelInfo = {
              modelName: manifest.modelName || null,
              modelJson: manifest.modelJson,
              deployedAt: manifest.deployedAt || null
            };
          }
        }
      }

      if (manifest && manifest.inputSize) {
        this.inputSize = manifest.inputSize;
      }
    } catch (_e) {
      // Use default path
    }

    console.log('[TFJS] Loading model from:', actualModelJsonUrl);
    console.log(`[TFJS] Model config: inputSize=${this.inputSize}, confThreshold=${this.confThreshold}, classes=${JSON.stringify(this.classNames)}`);

    try {
      this.model = await tf.loadGraphModel(actualModelJsonUrl);
      console.log('[TFJS] Loaded as GraphModel');
    } catch (_e) {
      this.model = await tf.loadLayersModel(actualModelJsonUrl);
      console.log('[TFJS] Loaded as LayersModel');
    }

    // Log model output shape info
    if (this.model.outputs) {
      const outputShapes = this.model.outputs.map(o => `${o.name}: ${JSON.stringify(o.shape)}`).join(', ');
      console.log(`[TFJS] Model outputs: ${outputShapes}`);
    }

    // Warmup - like reference implementation
    this._warmup();

    this.isInitialized = true;
    return true;
  }

  /**
   * Warmup inference to prime GPU pipeline
   */
  _warmup() {
    console.log('[TFJS] Running warmup...');
    const warmupInput = tf.zeros([1, this.inputSize, this.inputSize, NUM_CHANNELS]);
    const warmupOutput = this.model.predict(warmupInput);
    warmupInput.dispose();
    if (Array.isArray(warmupOutput)) {
      warmupOutput.forEach(t => t.dispose());
    } else if (warmupOutput instanceof tf.Tensor) {
      warmupOutput.dispose();
    } else {
      Object.values(warmupOutput).forEach(t => t.dispose && t.dispose());
    }
    console.log('[TFJS] Warmup complete');
  }

  /**
   * Preprocess image for YOLO26 inference with 3-channel RGB
   * Output: [1, H, W, 3] NHWC format, normalized 0-1
   *
   * Source can be a video element or canvas. Using canvas ensures detection
   * coordinates match the drawn frame exactly (fixes iOS Safari issues).
   */
  preprocess(source) {
    // Get source dimensions (canvas.width or video.videoWidth)
    const srcWidth = source.width || source.videoWidth;
    const srcHeight = source.height || source.videoHeight;

    // Log dimensions once for debugging
    if (!this._dimensionsLogged) {
      this._dimensionsLogged = true;
      console.log(`[TFJS] Source dimensions: ${srcWidth}x${srcHeight}`);
    }

    const scale = Math.min(this.inputSize / srcWidth, this.inputSize / srcHeight);
    const newWidth = Math.round(srcWidth * scale);
    const newHeight = Math.round(srcHeight * scale);
    const offsetX = (this.inputSize - newWidth) / 2;
    const offsetY = (this.inputSize - newHeight) / 2;

    // Store for postprocessing
    this._lastScale = {
      scale, offsetX, offsetY, srcWidth, srcHeight
    };

    const padTop = Math.floor(offsetY);
    const padBottom = Math.ceil(offsetY);
    const padLeft = Math.floor(offsetX);
    const padRight = Math.ceil(offsetX);

    return tf.tidy(() => {
      let tensor = tf.browser.fromPixels(source);

      tensor = tf.image.resizeBilinear(tensor, [newHeight, newWidth]);
      tensor = tf.pad(tensor, [
        [padTop, padBottom],
        [padLeft, padRight],
        [0, 0]
      ], 128);
      tensor = tensor.div(255.0);
      return tensor.expandDims(0);  // [1, H, W, 3] NHWC format
    });
  }

  async detect(source) {
    if (!this.isInitialized) {
      throw new Error('Detector not initialized. Call initialize() first.');
    }

    try {
      const inputTensor = this.preprocess(source);
      const rawOutput = await this.model.predict(inputTensor);

      // Handle different output formats (single tensor, array, or dict)
      // Model has two outputs: [TopK indices, Identity detections]
      // We want the Identity output which has shape [1, max_det, 6]
      let outputTensor;
      if (Array.isArray(rawOutput)) {
        // For YOLO end2end models, output[0] is TopK indices [1, max_det],
        // output[1] is Identity detections [1, max_det, 6]
        outputTensor = rawOutput.length > 1 && rawOutput[1].shape.length === 3
          ? rawOutput[1]  // Identity detections
          : rawOutput[0];
      } else if (rawOutput.data) {
        // Single tensor with .data() method
        outputTensor = rawOutput;
      } else {
        // Dict-like output (NamedTensorMap), prefer Identity:0 for detections
        const keys = Object.keys(rawOutput);
        const identityKey = keys.find(k => k.includes('Identity'));
        outputTensor = rawOutput[identityKey || keys[0]];
      }

      // Read full output - slicing on GPU causes sync overhead that's worse than reading 7.2KB
      // [1, 300, 6] = 1800 floats = 7.2KB, typically reads in 1-5ms
      const output = await outputTensor.data();
      const shape = outputTensor.shape;

      inputTensor.dispose();
      // Dispose all output tensors
      if (Array.isArray(rawOutput)) {
        rawOutput.forEach(t => t.dispose());
      } else if (rawOutput.dispose) {
        rawOutput.dispose();
      } else {
        Object.values(rawOutput).forEach(t => t.dispose && t.dispose());
      }

      return this.postprocess(output, shape);
    } catch (error) {
      console.error('[TFJS] Detection error:', error);
      return [];
    }
  }

  /**
   * Postprocess YOLO26 output - auto-detects format based on shape
   */
  postprocess(data, shape) {
    const [_batch, numPredictions, _features] = shape;

    // Detect format: end2end has <= 300 predictions (10, 50, 100, 300), raw has 2100
    if (numPredictions <= YOLO26_END2END_DETECTIONS) {
      return this.postprocessEnd2End(data, shape);
    } else {
      return this.postprocessRaw(data, shape);
    }
  }

  /**
   * Postprocess YOLO26 end2end output (NMS already done in model)
   * Output format: [batch, max_det, 6] where max_det is 10-300 and 6 = [x1, y1, x2, y2, score, class_id]
   */
  postprocessEnd2End(data, shape) {
    const [_batch, maxDet, _features] = shape;
    const { scale, offsetX, offsetY, srcWidth, srcHeight } = this._lastScale;

    const detections = [];

    for (let i = 0; i < maxDet; i++) {
      const baseIdx = i * YOLO26_OUTPUT_FEATURES;
      const x1_input = data[baseIdx + 0];
      const y1_input = data[baseIdx + 1];
      const x2_input = data[baseIdx + 2];
      const y2_input = data[baseIdx + 3];
      const score = data[baseIdx + 4];
      const classId = Math.round(data[baseIdx + 5]);

      if (score < this.confThreshold) continue;

      // Transform from model space (320x320) to source space
      const x1 = (x1_input - offsetX) / scale;
      const y1 = (y1_input - offsetY) / scale;
      const x2 = (x2_input - offsetX) / scale;
      const y2 = (y2_input - offsetY) / scale;

      // Use canvas dimensions (srcWidth/srcHeight) for bbox clamping
      // These match videoWidth/videoHeight which is what canvas.drawImage uses
      const bbox = {
        x: Math.max(0, Math.min(srcWidth, x1)),
        y: Math.max(0, Math.min(srcHeight, y1)),
        width: Math.max(0, Math.min(srcWidth - x1, x2 - x1)),
        height: Math.max(0, Math.min(srcHeight - y1, y2 - y1))
      };

      if (bbox.width <= 0 || bbox.height <= 0) continue;

      detections.push({
        class: this.classNames[classId] || `class_${classId}`,
        classIndex: classId,
        confidence: score,
        bbox
      });
    }

    detections.sort((a, b) => b.confidence - a.confidence);

    return detections;
  }

  /**
   * Postprocess YOLO26 raw output (needs NMS)
   * Output format: [batch, 2100, 6] where 6 = [x1, y1, x2, y2, score_class0, score_class1]
   */
  postprocessRaw(data, shape) {
    const [_batch, numPredictions, _features] = shape;
    const { scale, offsetX, offsetY, srcWidth, srcHeight } = this._lastScale;
    const numClasses = this.classNames.length;

    const detections = [];

    for (let i = 0; i < numPredictions; i++) {
      const baseIdx = i * YOLO26_OUTPUT_FEATURES;
      const x1_input = data[baseIdx + 0];
      const y1_input = data[baseIdx + 1];
      const x2_input = data[baseIdx + 2];
      const y2_input = data[baseIdx + 3];

      // Find best class score
      let maxScore = 0;
      let classId = 0;
      for (let c = 0; c < numClasses; c++) {
        const score = data[baseIdx + 4 + c];
        if (score > maxScore) {
          maxScore = score;
          classId = c;
        }
      }

      if (maxScore < this.confThreshold) continue;

      const x1 = (x1_input - offsetX) / scale;
      const y1 = (y1_input - offsetY) / scale;
      const x2 = (x2_input - offsetX) / scale;
      const y2 = (y2_input - offsetY) / scale;

      const bbox = {
        x: Math.max(0, Math.min(srcWidth, x1)),
        y: Math.max(0, Math.min(srcHeight, y1)),
        width: Math.max(0, Math.min(srcWidth - x1, x2 - x1)),
        height: Math.max(0, Math.min(srcHeight - y1, y2 - y1))
      };

      if (bbox.width <= 0 || bbox.height <= 0) continue;

      detections.push({
        class: this.classNames[classId] || `class_${classId}`,
        classIndex: classId,
        confidence: maxScore,
        bbox
      });
    }

    // Apply NMS since raw output doesn't have it
    return this.nms(detections);
  }

  /**
   * Non-Maximum Suppression
   */
  nms(detections) {
    if (detections.length === 0) return [];

    detections.sort((a, b) => b.confidence - a.confidence);
    const kept = [];

    while (detections.length > 0) {
      const best = detections.shift();
      kept.push(best);

      detections = detections.filter(det => {
        if (det.class !== best.class) return true;
        return this.iou(best.bbox, det.bbox) < this.iouThreshold;
      });
    }

    return kept;
  }

  /**
   * Calculate Intersection over Union
   */
  iou(box1, box2) {
    const x1 = Math.max(box1.x, box2.x);
    const y1 = Math.max(box1.y, box2.y);
    const x2 = Math.min(box1.x + box1.width, box2.x + box2.width);
    const y2 = Math.min(box1.y + box1.height, box2.y + box2.height);

    const intersection = Math.max(0, x2 - x1) * Math.max(0, y2 - y1);
    const area1 = box1.width * box1.height;
    const area2 = box2.width * box2.height;
    const union = area1 + area2 - intersection;

    return intersection / union;
  }

  getModelInfo() {
    return {
      ...this.modelInfo,
      backend: this.actualBackend || this.backend
    };
  }

  async stop() {
    if (this.model) {
      this.model.dispose();
      this.model = null;
    }
    this.isInitialized = false;
  }
}

export function createDetector(options = {}) {
  const modelPath = options.modelPath || 'models/best_web_model/model.json';
  return new TfjsYoloDetector(modelPath, {
    classNames: options.classNames || ['ball', 'paddle'],
    inputSize: options.inputSize || 320,
    confThreshold: options.confThreshold || 0.15,
    iouThreshold: options.iouThreshold || 0.45
  });
}

export default TfjsYoloDetector;
